import { parseChildrenArray } from '@/utils'
import { ReactNode } from 'react'
import { Option } from '../Select'
import { Typography } from '../Typography'
import { OptionNode } from './OptionNode'
import { CardSelectContainer, Container } from './styles'

export interface CardSelectProps {
  children: ReactNode
  disabled?: boolean
  error?: boolean
  max?: number
  multiple?: boolean
  onSelect?: ((value: string[]) => void) | ((value: string) => void)
  alignment?: 'vertical' | 'grid'
  value?: string[] | string
  centered?: boolean
  hoc?: (children: ReactNode, value: string) => ReactNode
}

export const CardSelect = ({
  children,
  disabled,
  error,
  max,
  multiple,
  onSelect,
  alignment = 'vertical',
  value = [],
  centered,
  hoc = (x) => x,
}: CardSelectProps) => {
  // @ts-expect-error parseChildrenArray is not typed
  const options = parseChildrenArray(children.filter(Boolean))

  const selected = (optionValue: string) => {
    if (multiple) {
      return value.includes(optionValue)
    }
    return value === optionValue
  }

  const handleSelect = (optionValue: string) => {
    if (multiple) {
      if (Array.isArray(value) && value.includes(optionValue)) {
        // Remove the value if it's already selected
        const newValue = value.filter((v) => v !== optionValue)
        ;(onSelect as (value: string[]) => void)(newValue)
      } else if (max === undefined || value.length < max) {
        // Add the value if we haven't reached the max
        const newValue = [...value, optionValue]
        ;(onSelect as (value: string[]) => void)(newValue)
      }
    } else {
      // Single select - just set the value
      if (typeof onSelect === 'function') {
        ;(onSelect as (value: string) => void)(optionValue)
      }
    }
  }

  return (
    <Container>
      <CardSelectContainer alignment={alignment}>
        {options.map(
          (
            {
              key,
              value: optionValue,
              children: text,
              disabled: optionDisabled,
              node,
            },
            i,
          ) =>
            node.type === Option
              ? hoc(
                  <OptionNode
                    key={key}
                    centered={centered}
                    disabled={disabled || optionDisabled}
                    error={error}
                    onClick={() => handleSelect(optionValue)}
                    selected={selected(optionValue)}
                    value={optionValue}
                    width={100 / options.length - (options.length - 1)}
                  >
                    <Typography ellipsis variant="placeholder">
                      {text}
                    </Typography>
                  </OptionNode>,
                  optionValue,
                )
              : Array.isArray(children)
              ? children[i]
              : null,
        )}
      </CardSelectContainer>
    </Container>
  )
}
